import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { apiFetch, ApiError } from '../lib/api';
import UnplannedWorkSection from '../components/sprint/UnplannedWorkSection';
import SprintHealthPanel from '../components/sprint/SprintHealthPanel';
import type { HealthStatus } from '../components/sprint/types';
import {
  ClosedSprintHistory, SprintHeader, SprintSelector, UnlinkedNotice,
} from '../components/sprint/BoardSprintPanels';
import { fetchSprints, type Sprint, type SprintsPayload } from '../lib/sprintsApi';
import { ALL_ACTIVE, CLOSED_SPRINTS, scopeBoardIssues, type BoardSelection } from '../lib/boardSprintScope';
import SprintActionBar, { type BoardNotice } from '../components/sprint/SprintActionBar';
import PlanNextSprintButton from '../components/PlanNextSprintButton';
import SpikeOutcomeEditor from '../components/SpikeOutcomeEditor';
import { fetchBacklog, recordSpikeOutcome, type BacklogItem, type SpikeOutcomeBody } from '../lib/backlogApi';
import { spikeRefusal, type SpikeRefusal } from '../lib/sprintActionsApi';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Issue {
  id: string;
  externalId: string | null;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  type: string;
  storyPoints: number | null;
  /** Legacy free text (the bridge's sprint name or an import's). Never used to filter (#2957 ruling 1). */
  sprintId: string | null;
  /** SL.L1a-1: the sprint link is this item's backlog_items.sprint_id, from GET /sprints. */
  backlogItemId: string | null;
  sprintRef: string | null;
  assigneeId: string | null;
  aiConfidence: string | null;
  createdAt: string;
}

type TimingStatus = 'ON_TRACK' | 'LATE' | 'RUNNING_LATE' | 'COMPLETED_ON_TIME' | 'COMPLETED_LATE';

/** SL.L1a-2: a move to `status` refused for a SPIKE with no outcome (ruling 4). */
type SpikePrompt = SpikeRefusal & { status: string };

interface SprintStoryTiming {
  id: string;
  timingStatus: TimingStatus | null;
  daysLate: number | null;
  executionStatus: string;
}

interface SprintSummary {
  totalStories: number;
  onTrack: number;
  late: number;
  runningLate: number;
  completedOnTime: number;
  completedLate: number;
  currentDay: number;
  totalDays: number;
  healthStatus: HealthStatus;
}

interface SprintStoriesResponse {
  stories: SprintStoryTiming[];
  total: number;
  statusCounts: Record<string, number>;
  sprintSummary: SprintSummary | null;
  estimationType?: 'STORY_POINTS' | 'EU_RU' | 'BOTH';
}

interface SpikesResponse {
  spikes: { id: string; estimatedPts: number }[];
  totalEstimatedPts: number;
}

const TIMING_STYLES: Record<TimingStatus, { bg: string; color: string; label: (d: number) => string }> = {
  ON_TRACK:          { bg: 'rgba(34,197,94,0.10)',  color: '#22c55e', label: () => 'ON TRACK' },
  LATE:              { bg: 'rgba(239,68,68,0.10)',   color: '#ef4444', label: (d) => d > 0 ? `LATE \u00B7 ${d}d` : 'LATE' },
  RUNNING_LATE:      { bg: 'rgba(245,158,11,0.10)',  color: '#f59e0b', label: () => 'RUNNING LATE' },
  COMPLETED_ON_TIME: { bg: 'transparent',            color: '#22c55e', label: () => 'DONE ON TIME' },
  COMPLETED_LATE:    { bg: 'rgba(148,163,184,0.08)', color: '#94a3b8', label: (d) => d > 0 ? `DONE \u00B7 ${d}d late` : 'DONE LATE' },
};

// ── Constants ─────────────────────────────────────────────────────────────────

/** No timing read for the shown scope yet (SL.L4a). */
const NO_TIMINGS: ReadonlyMap<string, SprintStoryTiming> = new Map();

/** GET /sprints' sprints; a malformed payload degrades to none (SL.L1a-1). */
const sprintsOf = (d: SprintsPayload | undefined): Sprint[] => (Array.isArray(d?.sprints) ? d.sprints : []);

const COLUMNS: { id: string; label: string; status: string }[] = [
  { id: 'todo',       label: 'Todo',        status: 'OPEN' },
  { id: 'inprogress', label: 'In Progress',  status: 'IN_PROGRESS' },
  { id: 'inreview',   label: 'In Review',    status: 'IN_REVIEW' },
  { id: 'done',       label: 'Done',         status: 'DONE' },
];

const STATUS_TO_COL: Record<string, string> = {
  OPEN:        'todo',
  IN_PROGRESS: 'inprogress',
  IN_REVIEW:   'inreview',
  DONE:        'done',
};
const COL_TO_STATUS: Record<string, string> = {
  todo:        'OPEN',
  inprogress:  'IN_PROGRESS',
  inreview:    'IN_REVIEW',
  done:        'DONE',
};

const PRIORITY_STYLE: Record<string, { bg: string; color: string }> = {
  P0: { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
  P1: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  P2: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
};

const TYPE_BORDER: Record<string, string> = {
  USER_STORY: 'var(--primary)',
  TASK:       'var(--accent, #06b6d4)',
  BUG:        'var(--error)',
  FEATURE:    'var(--success)',
};

// ── IssueCard ─────────────────────────────────────────────────────────────────

const PRIORITY_BORDER: Record<string, string> = {
  P0: '#ef4444',
  P1: '#f59e0b',
  P2: '#6b7280',
};

function IssueCard({
  issue,
  onClick,
  dragging = false,
  timingStatus,
  daysLate,
}: {
  issue: Issue;
  onClick?: () => void;
  dragging?: boolean;
  timingStatus?: TimingStatus | null;
  daysLate?: number | null;
}) {
  const priorityBorder = PRIORITY_BORDER[issue.priority ?? ''] ?? 'var(--border)';
  const pStyle = PRIORITY_STYLE[issue.priority ?? ''];

  return (
    <div
      onClick={onClick}
      data-testid="issue-card"
      data-priority={issue.priority ?? ''}
      style={{
        background:   'var(--surface)',
        border:       '1px solid var(--border)',
        borderLeft:   `4px solid ${priorityBorder}`,
        borderRadius: 'var(--radius-md)',
        padding:      '10px 12px',
        cursor:       dragging ? 'grabbing' : 'grab',
        boxShadow:    dragging ? 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.15))' : 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.08))',
        opacity:      dragging ? 0.95 : 1,
        userSelect:   'none',
        transition:   dragging ? 'none' : 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { if (!dragging) e.currentTarget.style.boxShadow = 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.12))'; }}
      onMouseLeave={e => { if (!dragging) e.currentTarget.style.boxShadow = 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.08))'; }}
    >
      {/* Story ID — small, muted, monospace */}
      <p style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 4px' }}>
        {issue.externalId ?? issue.id.slice(0, 8)}
      </p>

      {/* Title — prominent */}
      <p style={{
        fontFamily: 'var(--font-body)',
        fontSize:   13,
        fontWeight: 600,
        color:      'var(--text)',
        margin:     '0 0 8px',
        lineHeight: 1.4,
        display:    '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow:   'hidden',
      }}>
        {issue.title}
      </p>

      {/* Footer row: priority badge + timing + type + points */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {pStyle && (
            <span
              data-testid="priority-badge"
              style={{
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                background: pStyle.bg, color: pStyle.color,
              }}
            >
              {issue.priority}
            </span>
          )}
          {timingStatus && (() => {
            const tc = TIMING_STYLES[timingStatus];
            return (
              <span
                data-testid="timing-badge"
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 9999,
                  background: tc.bg, color: tc.color, letterSpacing: 0.3,
                  border: timingStatus === 'COMPLETED_ON_TIME' ? `1px solid ${tc.color}` : 'none',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {tc.label(daysLate ?? 0)}
              </span>
            );
          })()}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {issue.type.replace('_', ' ')}
          </span>
          {issue.aiConfidence != null && parseFloat(issue.aiConfidence) < 0.6 && (
            <span
              title={`Low confidence (${Math.round(parseFloat(issue.aiConfidence) * 100)}%) — may need PM validation`}
              style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', fontFamily: 'var(--font-mono)' }}
            >
              ⚠ {Math.round(parseFloat(issue.aiConfidence) * 100)}%
            </span>
          )}
          {issue.storyPoints != null && (
            <span style={{
              background:   'var(--primary)',
              color:        '#fff',
              borderRadius: '50%',
              width:        20,
              height:       20,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              fontSize:     10,
              fontWeight:   700,
              flexShrink:   0,
            }}>
              {issue.storyPoints}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── DraggableCard ─────────────────────────────────────────────────────────────

function DraggableCard({ issue, onClick, timingStatus, daysLate }: { issue: Issue; onClick: () => void; timingStatus?: TimingStatus | null; daysLate?: number | null }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: issue.id });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <IssueCard issue={issue} onClick={isDragging ? undefined : onClick} timingStatus={timingStatus} daysLate={daysLate} />
    </div>
  );
}

// ── DroppableColumn ───────────────────────────────────────────────────────────

function DroppableColumn({
  col,
  issues,
  onCardClick,
  timingMap,
}: {
  col: typeof COLUMNS[number];
  issues: Issue[];
  onCardClick: (issue: Issue) => void;
  timingMap?: ReadonlyMap<string, SprintStoryTiming>;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Column header */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          8,
        marginBottom: 10,
        paddingBottom: 10,
        borderBottom: '2px solid var(--border)',
      }}>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
          {col.label}
        </span>
        <span style={{
          background:   'var(--surface-alt, var(--border))',
          color:        'var(--text-secondary)',
          borderRadius: 99,
          padding:      '1px 8px',
          fontSize:     11,
          fontWeight:   600,
        }}>
          {issues.length}
        </span>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        style={{
          flex:         1,
          minHeight:    120,
          background:   isOver ? 'rgba(124,58,237,0.04)' : 'transparent',
          border:       isOver ? '2px dashed var(--primary)' : '2px dashed transparent',
          borderRadius: 'var(--radius-lg)',
          padding:      isOver ? 4 : 0,
          transition:   'background 0.15s, border-color 0.15s',
          display:      'flex',
          flexDirection: 'column',
          gap:          8,
        }}
      >
        {issues.map(issue => {
          const timing = timingMap?.get(issue.id);
          return (
            <DraggableCard
              key={issue.id}
              issue={issue}
              onClick={() => onCardClick(issue)}
              timingStatus={timing?.timingStatus}
              daysLate={timing?.daysLate}
            />
          );
        })}
        {issues.length === 0 && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 24, opacity: 0.5 }}>
            Drop here
          </p>
        )}
      </div>
    </div>
  );
}

// ── IssueDetailPanel ──────────────────────────────────────────────────────────

function IssueDetailPanel({
  issue,
  projectId,
  onClose,
  onUpdate,
  loadBacklogItem,
  initialPrompt,
  initialError,
}: {
  issue: Issue;
  projectId: string;
  onClose: () => void;
  onUpdate: (updated: Partial<Issue>) => void;
  /** GET /backlog's row for a SPIKE prompt; read once per board, when first needed. */
  loadBacklogItem: (itemId: string) => Promise<BacklogItem | null>;
  /** A refused drag opens the drawer at its SPIKE prompt, with the server's message. */
  initialPrompt?: SpikePrompt;
  initialError?: string;
}) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState<Issue>(issue);
  const [devAgentLoading, setDevAgentLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(initialError ?? null);
  const [prompt, setPrompt] = useState<SpikePrompt | null>(initialPrompt ?? null);
  // undefined while the item loads; null when the backlog doesn't hold it.
  const [promptItem, setPromptItem] = useState<BacklogItem | null | 'failed' | undefined>(undefined);
  // The refused PATCH is retried once per drawer, after the outcome saves (ruling 4).
  const retried = useRef(false);

  const loadPromptItem = useCallback((p: SpikePrompt) => {
    loadBacklogItem(p.backlogItemId).then(setPromptItem, () => setPromptItem('failed'));
  }, [loadBacklogItem]);
  useEffect(() => { if (initialPrompt) loadPromptItem(initialPrompt); }, [initialPrompt, loadPromptItem]);

  // #3055 — a rejected save (e.g. #2948's DONE/CANCELLED role gate) shows the
  // server's reason. Only the selects pass onFail to revert; text fields keep
  // what the user typed, and the next blur retries.
  async function save(fields: Partial<Issue>, onFail?: () => void): Promise<boolean> {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await apiFetch<Issue>(`/issues/${issue.id}`, {
        method: 'PATCH',
        body: JSON.stringify(fields),
      });
      setLocal(prev => ({ ...prev, ...updated }));
      onUpdate(updated);
      return true;
    } catch (err) {
      onFail?.();
      setSaveError(err instanceof ApiError && err.message ? err.message : 'Failed to save changes');
      // SL.L1a-2 (ruling 4): matched on the 409's code, never its text. The
      // backlog row is redacted for a CLIENT, so nothing is checked before.
      const spike = spikeRefusal(err);
      if (spike) {
        const p = { ...spike, status: String(fields.status) };
        setPrompt(p);
        setPromptItem(undefined);
        loadPromptItem(p);
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Record the outcome, then retry the refused move once. Without
  // record_outcome the editor is read-only, so this never runs.
  async function saveOutcome(p: SpikePrompt, item: BacklogItem, body: SpikeOutcomeBody) {
    await recordSpikeOutcome(projectId, p.backlogItemId, body);
    // The body names outcomeAdrId only when it changed, as the server reads it.
    setPromptItem({ ...item, ...body });
    if (retried.current) return;
    retried.current = true;
    if (await saveSelect('status', p.status)) setPrompt(null);
  }

  // Revert only if the select still shows the value this request tried to set
  // — a newer change may already have replaced it. Revert to the issue prop,
  // which BoardPage updates only on a successful save: `local` may hold an
  // earlier attempt that is still in flight and about to fail too.
  function saveSelect(key: 'status' | 'priority' | 'type', value: string | null): Promise<boolean> {
    const previous = issue[key];
    setLocal(p => ({ ...p, [key]: value }));
    return save({ [key]: value }, () =>
      setLocal(p => (p[key] === value ? { ...p, [key]: previous } : p)));
  }

  const fieldLabel: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 4, display: 'block',
  };
  const fieldValue: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', padding: '6px 10px',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    width: '100%', boxSizing: 'border-box',
  };

  return (
    <div style={{
      position:   'fixed', top: 0, right: 0, bottom: 0, width: 400,
      background: 'var(--surface)',
      borderLeft: '1px solid var(--border)',
      boxShadow:  '-4px 0 24px rgba(0,0,0,0.12)',
      zIndex:     50,
      display:    'flex',
      flexDirection: 'column',
      overflowY:  'auto',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
          {local.externalId ?? local.id.slice(0, 8)}
        </span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-secondary)', padding: '0 4px' }}>✕</button>
      </div>

      {/* Save error — sticky so it stays in view while the drawer scrolls */}
      {saveError && (
        <div
          data-testid="drawer-save-error"
          style={{
            position: 'sticky', top: 0, zIndex: 1, flexShrink: 0,
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--border)',
          }}
        >
          <span role="alert" style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
            {saveError}
          </span>
          <button
            aria-label="Dismiss error"
            onClick={() => setSaveError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)', padding: '0 2px' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Body */}
      <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Title */}
        <div>
          <label style={fieldLabel}>Title</label>
          <input
            style={fieldValue}
            defaultValue={local.title}
            onBlur={e => { if (e.target.value !== local.title) void save({ title: e.target.value }); }}
          />
        </div>

        {/* Status + Priority row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={fieldLabel}>Status</label>
            <select
              style={{ ...fieldValue, cursor: 'pointer' }}
              value={local.status}
              onChange={e => void saveSelect('status', e.target.value)}
            >
              {['OPEN', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED'].map(s => (
                <option key={s} value={s}>{s.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={fieldLabel}>Priority</label>
            <select
              style={{ ...fieldValue, cursor: 'pointer' }}
              value={local.priority ?? ''}
              onChange={e => void saveSelect('priority', e.target.value || null)}
            >
              <option value="">—</option>
              {['P0', 'P1', 'P2', 'P3'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {/* SL.L1a-2: the SPIKE outcome the refused move needs */}
        {prompt && (
          <div data-testid="spike-prompt">
            {promptItem === undefined ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>{`Loading ${prompt.key}…`}</p>
            ) : promptItem === 'failed' || promptItem === null ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', margin: 0 }}>{`Couldn't load ${prompt.key} from the backlog.`}</p>
            ) : (
              <SpikeOutcomeEditor item={promptItem} startOpen onSave={body => saveOutcome(prompt, promptItem, body)} />
            )}
          </div>
        )}

        {/* Type + Points row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={fieldLabel}>Type</label>
            <select
              style={{ ...fieldValue, cursor: 'pointer' }}
              value={local.type}
              onChange={e => void saveSelect('type', e.target.value)}
            >
              {['USER_STORY', 'TASK', 'BUG', 'FEATURE'].map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label style={fieldLabel}>Story Points</label>
            <input
              type="number"
              min="0"
              step="1"
              style={fieldValue}
              defaultValue={local.storyPoints ?? ''}
              onBlur={e => {
                const v = e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value)));
                if (v !== local.storyPoints) void save({ storyPoints: v });
              }}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={fieldLabel}>Description</label>
          <textarea
            style={{ ...fieldValue, minHeight: 120, resize: 'vertical', fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.5 }}
            defaultValue={local.description ?? ''}
            onBlur={e => { if (e.target.value !== (local.description ?? '')) void save({ description: e.target.value }); }}
          />
        </div>

        {/* Open in Dev Agent */}
        <button
          disabled={devAgentLoading}
          onClick={async () => {
            setDevAgentLoading(true);
            try {
              // Check for active Dev Agent session
              const sessions = await apiFetch<any[]>(
                `/projects/${projectId}/sessions?agentType=DEV_AGENT&limit=1`,
              );
              const activeSession = Array.isArray(sessions)
                ? sessions.find((s: any) => s.status === 'active')
                : null;

              if (activeSession) {
                // Patch existing session with this issue
                await apiFetch(`/projects/${projectId}/sessions/${activeSession.id}/issue`, {
                  method: 'PATCH',
                  body: JSON.stringify({ issueId: issue.id }),
                });
                navigate(`/projects/${projectId}/chat?sessionId=${activeSession.id}`);
              } else {
                // Create new session with issue pre-selected
                const data = await apiFetch<{ id: string }>(`/projects/${projectId}/sessions`, {
                  method: 'POST',
                  body: JSON.stringify({ agentType: 'DEV_AGENT', issueId: issue.id, forceNew: false }),
                });
                const sessionId = (data as any).id;
                navigate(`/projects/${projectId}/chat?sessionId=${sessionId}`);
              }
            } catch {
              // silently fail — user can retry
            } finally {
              setDevAgentLoading(false);
            }
          }}
          style={{
            width: '100%', marginTop: 4, padding: '10px 16px',
            background: devAgentLoading ? '#6d28d9' : '#7c3aed',
            color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            cursor: devAgentLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: devAgentLoading ? 0.7 : 1,
          }}
          onMouseOver={e => { if (!devAgentLoading) (e.currentTarget.style.background = '#6d28d9'); }}
          onMouseOut={e => { if (!devAgentLoading) (e.currentTarget.style.background = '#7c3aed'); }}
        >
          {devAgentLoading ? 'Opening...' : (<><span>⚡</span> Open in Dev Agent →</>)}
        </button>

        {saving && <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Saving…</p>}
      </div>
    </div>
  );
}

// ── BoardPage ─────────────────────────────────────────────────────────────────

export default function BoardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [issues, setIssues]           = useState<Issue[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null);   // dnd overlay
  const [detailIssue, setDetailIssue] = useState<Issue | null>(null);   // side panel
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType]         = useState('');
  const [filterSearch, setFilterSearch]     = useState('');
  const [toastMsg, setToastMsg]       = useState<string | null>(null);
  const [estimationType, setEstimationType] = useState<'STORY_POINTS' | 'EU_RU' | 'BOTH'>('STORY_POINTS');
  // SL.L4a (#2960): the timing answer, tagged with the project and selection it
  // was read for, so a scope never shows another scope's figures.
  // Fix round R3-2 (6278525023): bumped by reload(), so a sprint action
  // re-reads the timings of the selection on screen.
  const [timingNonce, setTimingNonce]     = useState(0);
  const [storyTiming, setStoryTiming]     = useState<{ scope: string; summary: SprintSummary | null; timings: Map<string, SprintStoryTiming> } | null>(null);
  const [spikeCount, setSpikeCount]       = useState(0);
  const [spikePts, setSpikePts]           = useState(0);
  const [sprints, setSprints]             = useState<Sprint[]>([]);
  const [sprintsError, setSprintsError]   = useState<string | null>(null);
  const [selection, setSelection]         = useState<BoardSelection>(ALL_ACTIVE);
  const [projectActions, setProjectActions] = useState<readonly string[] | undefined>(undefined);
  const [notice, setNotice]               = useState<BoardNotice | null>(null);
  // A refused drag opens the drawer at the SPIKE prompt; the nonce remounts it.
  const [drawerSeed, setDrawerSeed]       = useState<{ nonce: number; prompt: SpikePrompt; error: string } | null>(null);
  const backlogRef = useRef<{ projectId: string; items: Promise<Map<string, BacklogItem>> } | null>(null);
  // The project on screen, for writes that land after an await (fix round R1-2).
  const projectRef = useRef(projectId);
  useEffect(() => { projectRef.current = projectId; }, [projectId]);

  // Another project starts from All active, with no stale sprints or error
  // (fix round 6255091113 item 5). Adjusted during render, not in an effect.
  const [sprintsFor, setSprintsFor] = useState(projectId);
  if (sprintsFor !== projectId) {
    setSprintsFor(projectId);
    setSelection(ALL_ACTIVE);
    setSprintsError(null);
    setSprints([]);
    setProjectActions(undefined);
    setNotice(null);
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!projectId) return;
    // SL.L1a-2 (re-probe 6255525187 P3 1): a response for a previous project
    // never lands. Every read below goes through `live`.
    let ignore = false;
    const live = <T,>(apply: (v: T) => void) => (v: T) => { if (!ignore) apply(v); };

    apiFetch<Issue[]>(`/projects/${projectId}/issues`)
      .then(live(d => { setIssues(d); setLoading(false); }))
      .catch(live(e => { setError((e as Error).message); setLoading(false); }));

    apiFetch<SpikesResponse>(`/projects/${projectId}/sprints/current/spikes`)
      .then(live(d => { setSpikeCount(d.spikes.length); setSpikePts(d.totalEstimatedPts); }))
      .catch(() => {});

    // SL.L1a-1: the sprints drive the selector, header and filter. A failure
    // is shown, and the board falls back to every issue, as before (#3055).
    fetchSprints(projectId)
      .then(live(d => {
        setSprints(sprintsOf(d));
        setProjectActions(d?.allowedActions);
      }))
      .catch(live(e => setSprintsError((e as Error).message || 'Failed to load sprints')));
    return () => { ignore = true; };
  }, [projectId]);

  // SL.L4a (#2960, routing 6255091283): the timing read follows the selection,
  // so the health panel and the summary bar agree. A single sprint reads its
  // own stories; All active keeps /sprints/current/stories; Closed sprints
  // shows no panel and reads nothing. Non-blocking — the panel just won't
  // render if it fails. A response for an earlier selection or project never
  // lands (the load effect's `live` rule).
  const timingScope = `${projectId}:${selection}`;
  useEffect(() => {
    if (!projectId || selection === CLOSED_SPRINTS) return;
    let ignore = false;
    const live = <T,>(apply: (v: T) => void) => (v: T) => { if (!ignore) apply(v); };
    const path = selection === ALL_ACTIVE ? 'current' : encodeURIComponent(selection);
    apiFetch<SprintStoriesResponse>(`/projects/${projectId}/sprints/${path}/stories`)
      .then(live(d => {
        if (d.estimationType) setEstimationType(d.estimationType);
        const timings = new Map<string, SprintStoryTiming>();
        for (const s of d.stories) timings.set(s.id, s);
        setStoryTiming({ scope: `${projectId}:${selection}`, summary: d.sprintSummary, timings });
      }))
      .catch(() => {});
    return () => { ignore = true; };
  }, [projectId, selection, timingNonce]);
  const shownTiming = storyTiming?.scope === timingScope ? storyTiming : null;
  const sprintSummary = shownTiming?.summary ?? null;
  const timingMap = shownTiming?.timings ?? NO_TIMINGS;

  // SL.L1a-2: after a sprint action, re-read what it changed. Never rejects:
  // a failed sprints read shows as before; a failed issues read keeps the
  // issues on screen. A response that lands after a project switch writes
  // nothing (fix round R1-2, the load effect's rule).
  const reload = useCallback(async ({ issues: withIssues }: { issues: boolean }) => {
    const live = <T,>(apply: (v: T) => void) => (v: T) => { if (projectRef.current === projectId) apply(v); };
    setTimingNonce(n => n + 1);
    await Promise.all([
      fetchSprints(projectId!).then(live(d => {
        setSprints(sprintsOf(d));
        setProjectActions(d?.allowedActions);
        setSprintsError(null);
      }), live(e => setSprintsError((e as Error).message || 'Failed to load sprints'))),
      withIssues ? apiFetch<Issue[]>(`/projects/${projectId}/issues`).then(live(setIssues), () => {}) : null,
    ]);
  }, [projectId]);

  // An action's announcement for a project no longer on screen is dropped.
  const announce = useCallback((n: BoardNotice) => {
    if (projectRef.current === projectId) setNotice(n);
  }, [projectId]);

  // The SPIKE prompt's item: GET /backlog once per project, when a prompt
  // first needs it (ruling 4), never on board load. A failed read isn't kept.
  const loadBacklogItem = useCallback((itemId: string): Promise<BacklogItem | null> => {
    let entry = backlogRef.current;
    if (!entry || entry.projectId !== projectId) {
      const items = fetchBacklog(projectId!).then(d => new Map(d.items.map(i => [i.id, i])));
      const created = { projectId: projectId!, items };
      items.catch(() => { if (backlogRef.current === created) backlogRef.current = null; });
      backlogRef.current = entry = created;
    }
    return entry.items.then(m => m.get(itemId) ?? null);
  }, [projectId]);

  // With no sprints, every issue is unlinked, so the scope is every issue.
  const hasSprints = sprints.length > 0;
  const scoped = scopeBoardIssues(issues, sprints, selection);
  const legacySprintName = issues.find(i => i.sprintId)?.sprintId ?? 'Sprint 1';
  const visibleIssues = scoped.shown.filter(i => i.status !== 'CANCELLED').filter(i => {
    if (filterPriority && i.priority !== filterPriority) return false;
    if (filterType && i.type !== filterType) return false;
    if (filterSearch && !i.title.toLowerCase().includes(filterSearch.toLowerCase())) return false;
    return true;
  });

  // Sprint summary stats (over the selected sprint scope, before the filters)
  const allActive = scoped.shown.filter(i => i.status !== 'CANCELLED');
  const doneCount = allActive.filter(i => i.status === 'DONE').length;
  const inProgCount = allActive.filter(i => i.status === 'IN_PROGRESS').length;
  const todoCount = allActive.filter(i => i.status === 'OPEN').length;
  const totalPts = allActive.reduce((s, i) => s + (i.storyPoints ?? 0), 0);
  const donePts  = allActive.filter(i => i.status === 'DONE').reduce((s, i) => s + (i.storyPoints ?? 0), 0);
  const pct = allActive.length === 0 ? 0 : Math.round((doneCount / allActive.length) * 100);

  function showToast(msg: string) {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  }

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const issue = issues.find(i => i.id === event.active.id);
    if (issue) setActiveIssue(issue);
  }, [issues]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveIssue(null);
    const { active, over } = event;
    if (!over) return;

    const issue = issues.find(i => i.id === active.id);
    if (!issue) return;

    const newColId = String(over.id);
    const newStatus = COL_TO_STATUS[newColId];
    if (!newStatus || newStatus === issue.status) return;

    const prevStatus = issue.status;

    // Optimistic update
    setIssues(prev => prev.map(i => i.id === issue.id ? { ...i, status: newStatus } : i));

    try {
      await apiFetch(`/issues/${issue.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (err) {
      // Revert
      setIssues(prev => prev.map(i => i.id === issue.id ? { ...i, status: prevStatus } : i));
      // #2948 — show the server's reason (e.g. only the assignee, Tech Lead or
      // PM may set DONE/CANCELLED); fall back when there is no message.
      const message = err instanceof ApiError && err.message ? err.message : 'Failed to update issue status';
      showToast(message);
      // SL.L1a-2 (ruling 4): a SPIKE with no outcome opens the drawer at its editor.
      const spike = spikeRefusal(err);
      if (spike) {
        setDrawerSeed(prev => ({ nonce: (prev?.nonce ?? 0) + 1, prompt: { ...spike, status: newStatus }, error: message }));
        setDetailIssue(issue);
      }
    }
  }, [issues]);

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
  };

  return (
    <>
      {/* Toast */}
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--error)', color: '#fff',
          padding: '8px 20px', borderRadius: 8, fontSize: 13,
          fontFamily: 'var(--font-body)', zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toastMsg}
        </div>
      )}

      <main style={{ flex: 1, padding: 'var(--space-8)', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Link
              to={`/projects/${projectId}`}
              style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
            >
              ← Dashboard
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Sprint Board
            </h1>
            {hasSprints ? (
              <SprintSelector sprints={sprints} value={selection} onChange={setSelection} />
            ) : (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
                {legacySprintName}
              </span>
            )}
            {/* L2's button, from the project-level actions (ruling 11); it doesn't navigate. */}
            <PlanNextSprintButton projectId={projectId!} allowedActions={projectActions} onPlanned={() => reload({ issues: false })} />
          </div>
          {hasSprints && (
            <SprintHeader
              sprints={sprints}
              selection={selection}
              renderActions={s => <SprintActionBar projectId={projectId!} sprint={s} reload={reload} onNotice={announce} />}
            />
          )}
          {notice && (
            <p
              role={notice.error ? 'alert' : 'status'}
              style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: notice.error ? 'var(--error)' : 'var(--text-secondary)', margin: '6px 0 0' }}
            >
              {notice.text}
            </p>
          )}
          {sprintsError && (
            <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', margin: '6px 0 0' }}>
              {`Couldn't load this project's sprints: ${sprintsError}. Showing every issue.`}
            </p>
          )}
        </div>

        {hasSprints && (
          <UnlinkedNotice count={scoped.unlinked} selection={selection} onShowAllActive={() => setSelection(ALL_ACTIVE)} />
        )}

        {loading ? (
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Loading issues…</p>
        ) : error ? (
          <p style={{ color: 'var(--error)', fontFamily: 'var(--font-body)' }}>{error}</p>
        ) : selection === CLOSED_SPRINTS ? (
          <ClosedSprintHistory projectId={projectId!} sprints={sprints} />
        ) : issues.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
              No sprint issues yet.{' '}
              <Link to={`/artefacts?projectId=${projectId}`} style={{ color: 'var(--primary)' }}>
                Approve a sprint backlog
              </Link>{' '}
              to populate the board.
            </p>
          </div>
        ) : (
          <>
            {/* Sprint Summary Bar */}
            <div style={{ ...cardStyle, marginBottom: 'var(--space-5)', padding: '14px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', marginBottom: 8 }}>
                    {(sprintSummary ? [
                      { label: `${doneCount} done`, color: 'var(--success)' },
                      { label: `${sprintSummary.onTrack} on track`, color: '#22c55e' },
                      { label: `${sprintSummary.late + sprintSummary.runningLate} late`, color: '#ef4444' },
                      { label: `${allActive.filter(i => timingMap.get(i.id)?.executionStatus === 'BLOCKED').length} blocked`, color: 'var(--warning)' },
                    ] : [
                      { label: `${doneCount} / ${allActive.length} done`, color: 'var(--success)' },
                      { label: `${donePts} / ${totalPts} pts`, color: 'var(--primary)' },
                      { label: `${inProgCount} in progress`, color: 'var(--warning)' },
                      { label: `${todoCount} todo`, color: 'var(--text-secondary)' },
                    ]).map(({ label, color }) => (
                      <span key={label} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color }}>{label}</span>
                    ))}
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden', position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: 'var(--success)', borderRadius: 3, transition: 'width 0.3s' }} />
                    <div style={{ position: 'absolute', left: `${pct}%`, top: 0, height: '100%', width: `${Math.round((inProgCount / Math.max(allActive.length, 1)) * 100)}%`, background: 'var(--primary)', opacity: 0.6, transition: 'width 0.3s' }} />
                  </div>
                </div>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                  {pct}%
                </span>
              </div>
            </div>

            {/* Sprint Health Panel */}
            {sprintSummary && (
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <SprintHealthPanel
                  sprintDay={sprintSummary.currentDay}
                  sprintTotal={sprintSummary.totalDays}
                  ptsDone={donePts}
                  ptsRemaining={totalPts - donePts}
                  spikeCount={spikeCount}
                  spikePts={spikePts}
                  healthStatus={sprintSummary.healthStatus}
                  totalPoints={totalPts}
                  estimationType={estimationType}
                />
              </div>
            )}

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
              {[
                { label: 'Priority', value: filterPriority, setter: setFilterPriority, options: ['P0','P1','P2','P3'] },
                { label: 'Type', value: filterType, setter: setFilterType, options: ['USER_STORY','TASK','BUG','FEATURE'] },
              ].map(({ label, value, setter, options }) => (
                <select
                  key={label}
                  value={value}
                  onChange={e => setter(e.target.value)}
                  style={{
                    fontFamily: 'var(--font-body)', fontSize: 13,
                    padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    color: 'var(--text)', cursor: 'pointer',
                  }}
                >
                  <option value="">All {label}</option>
                  {options.map(o => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
                </select>
              ))}
              <input
                type="text"
                placeholder="Search…"
                value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13,
                  padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'var(--surface)',
                  color: 'var(--text)', minWidth: 160,
                }}
              />
              {(filterPriority || filterType || filterSearch) && (
                <button
                  onClick={() => { setFilterPriority(''); setFilterType(''); setFilterSearch(''); }}
                  style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 4px' }}
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Kanban Columns */}
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div style={{ display: 'flex', gap: 16, flex: 1, alignItems: 'flex-start', overflowX: 'auto' }}>
                {COLUMNS.map(col => (
                  <DroppableColumn
                    key={col.id}
                    col={col}
                    issues={visibleIssues.filter(i => STATUS_TO_COL[i.status] === col.id)}
                    onCardClick={i => { setDrawerSeed(null); setDetailIssue(i); }}
                    timingMap={timingMap}
                  />
                ))}
              </div>

              <DragOverlay>
                {activeIssue && <IssueCard issue={activeIssue} dragging />}
              </DragOverlay>
            </DndContext>

            {/* Unplanned Work (spikes) */}
            <UnplannedWorkSection projectId={projectId!} />
          </>
        )}
      </main>

      {/* Issue Detail Panel */}
      {detailIssue && (
        <IssueDetailPanel
          key={`${detailIssue.id}:${drawerSeed?.nonce ?? 0}`}
          issue={detailIssue}
          projectId={projectId!}
          loadBacklogItem={loadBacklogItem}
          initialPrompt={drawerSeed?.prompt}
          initialError={drawerSeed?.error}
          onClose={() => { setDetailIssue(null); setDrawerSeed(null); }}
          onUpdate={updated => {
            setIssues(prev => prev.map(i => i.id === detailIssue.id ? { ...i, ...updated } : i));
            setDetailIssue(prev => prev ? { ...prev, ...updated } : null);
          }}
        />
      )}
    </>
  );
}
